import { QueryClient, notifyManager, type QueryClientConfig } from '@tanstack/react-query';

let configured = false;

/**
 * ★★ react-query TELLS REACT ABOUT A CACHE CHANGE ON A MACROTASK, AND FOR A DRAG THAT IS TOO LATE.
 *
 * `notifyManager`'s default scheduler is `systemSetTimeoutZero` — a `setTimeout(fn, 0)`. Every
 * `setQueryData` therefore reaches its observers in a LATER task, and the browser is free to paint
 * in between. For ordinary reads nobody can see it. For an optimistic update applied as the user
 * lets go of a dragged row, that gap is the whole defect: the row is released, the old order paints
 * once, and only then does React hear about the new one — UX-823's remaining "it still jerks once"
 * after the optimistic update was already in place and correct.
 *
 * A microtask closes it. It still runs after the current call stack (so no re-entrancy, and
 * batching within a task is unchanged), but always before the next paint, so the drop and the
 * reordered list land in the same frame.
 *
 * Global on purpose — `notifyManager` is a module-level singleton shared by every QueryClient, so
 * this is set once for whichever shell booted rather than per client. It is idempotent so the
 * three shells (hub/studio/delivery) and the tests can all call it.
 */
export function configureQueryNotifications() {
    if (configured) return;
    configured = true;
    notifyManager.setScheduler(callback => queueMicrotask(callback));
}

/**
 * The app's QueryClient. Use this rather than `new QueryClient()` so the notification scheduler
 * above is in force — a client built directly still works, it just repaints a frame late.
 */
export function createQueryClient(config?: QueryClientConfig) {
    configureQueryNotifications();
    return new QueryClient(config);
}
